/**
 * Produktové kategorie Materiály / Produkty — strom s kořeny:
 * - **Obecné** — produkty nevázané na typ objektu; podsložky = strom z listu
 *   *Komponenty* (`components.hlavni_kategorie` → `rozvetveni_4`, sync z Excelu)
 * - **Spojkoviště / Budova / Výkop** — podle typu objektu (mapa / filtr zásob)
 *
 * Mapový picker filtruje podle větve kořene odvozené z `object.type`
 * (JAMA → VYKOP). Obecné na mapě typ objektu nemá.
 */

import { getObjectTypeName } from './object-type-labels.constants';
import { normalizeObjectTypeForLookup } from './object-workflow/phase-codes';

/** Kořeny vázané na typ objektu (mapa). */
export type ProductCategoryObjectTypeKey = 'SPOJKOVISTE' | 'BUDOVA' | 'VYKOP';

/** Všechny systémové kořeny včetně Obecné. */
export type ProductCategoryRootKey = ProductCategoryObjectTypeKey | 'OBECNE';

/** Pořadí kořenů vázaných na typ objektu (mapa). */
export const PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS: readonly ProductCategoryObjectTypeKey[] = [
  'SPOJKOVISTE',
  'BUDOVA',
  'VYKOP',
] as const;

/**
 * Mapování `object.type` (backend) → kořen produktové kategorie.
 * Typy bez záznamu → undefined (plný strom katalogu).
 */
export const OBJECT_TYPE_TO_PRODUCT_CATEGORY_ROOT: Partial<
  Record<string, ProductCategoryObjectTypeKey>
> = {
  SPOJKOVISTE: 'SPOJKOVISTE',
  BUDOVA: 'BUDOVA',
  VYKOP: 'VYKOP',
  /** JAMA sdílí větev s VYKOP (stejné povrchy / výkopová práce). */
  JAMA: 'VYKOP',
};

/** Stabilní UUID kořenů (seed migrace 138 + 139 Obecné). */
export const PRODUCT_CATEGORY_ROOT_IDS: Record<ProductCategoryRootKey, string> = {
  OBECNE: 'a1000001-0001-4000-8000-000000000000',
  SPOJKOVISTE: 'a1000001-0001-4000-8000-000000000001',
  BUDOVA: 'a1000001-0001-4000-8000-000000000002',
  VYKOP: 'a1000001-0001-4000-8000-000000000003',
};

/** Pořadí všech systémových kořenů v UI (Obecné první). */
export const PRODUCT_CATEGORY_ALL_ROOT_KEYS: readonly ProductCategoryRootKey[] = [
  'OBECNE',
  ...PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS,
] as const;

/** České popisky kořenů vázaných na typ objektu. */
export const PRODUCT_CATEGORY_OBJECT_TYPE_ROOT_LABELS: Record<
  ProductCategoryObjectTypeKey,
  string
> = {
  SPOJKOVISTE: getObjectTypeName('SPOJKOVISTE'),
  BUDOVA: getObjectTypeName('BUDOVA'),
  VYKOP: `${getObjectTypeName('VYKOP')} (vč. ${getObjectTypeName('JAMA')})`,
};

/** České popisky všech kořenů. */
export const PRODUCT_CATEGORY_ROOT_LABELS: Record<ProductCategoryRootKey, string> = {
  OBECNE: 'Obecné',
  ...PRODUCT_CATEGORY_OBJECT_TYPE_ROOT_LABELS,
};

/** object.type → kořen produktové kategorie (JAMA → VYKOP); Obecné se z typu neodvozuje. */
export function getProductCategoryRootKeyForObjectType(
  objectType: string | null | undefined,
): ProductCategoryObjectTypeKey | undefined {
  if (!objectType) return undefined;
  const key = normalizeObjectTypeForLookup(objectType.trim());
  return OBJECT_TYPE_TO_PRODUCT_CATEGORY_ROOT[key];
}

/** Je `raw` platný kořen vázaný na typ objektu? */
export function parseProductCategoryObjectTypeKey(
  raw: string | null | undefined,
): ProductCategoryObjectTypeKey | undefined {
  const key = String(raw ?? '')
    .trim()
    .toUpperCase();
  return (PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS as readonly string[]).includes(key)
    ? (key as ProductCategoryObjectTypeKey)
    : undefined;
}

/** Normalizuje segmenty cesty kategorie (max 6, prázdné / `-` vynechá). */
export function normalizeProductCategoryPathSegments(
  path: readonly string[] | null | undefined,
): string[] {
  if (!path?.length) return [];
  const out: string[] = [];
  for (const raw of path) {
    const seg = String(raw ?? '').trim();
    if (!seg || seg === '-') break;
    out.push(seg);
    if (out.length >= 6) break;
  }
  return out;
}

/** Normalizace segmentu cesty kategorie (trim + kolaps mezer). Placeholder `-` = prázdné. */
export function normalizeProductCategorySegment(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null;
  const t = String(raw).trim().replace(/[\\/]+$/g, '').trim();
  if (t === '' || t === '-') return null;
  return t.replace(/\s+/g, ' ');
}

/** Oddělovač segmentů v import/export sloupci Zařazení. */
export const COMPONENT_CATEGORY_PATH_SEPARATOR = '/';

/** Uvnitř segmentu se `/` escapuje jako `\/` (Excel round-trip pro např. `7/4`). */
const COMPONENT_CATEGORY_PATH_SLASH_ESCAPE = '\\/';

function escapeComponentCategoryPathSegment(segment: string): string {
  return segment.replace(/\//g, COMPONENT_CATEGORY_PATH_SLASH_ESCAPE);
}

function unescapeComponentCategoryPathSegment(segment: string): string {
  return segment.replace(/\\\//g, '/');
}

function splitComponentCategoryPath(path: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < path.length; i++) {
    const ch = path[i];
    if (ch === '\\' && path[i + 1] === '/') {
      current += '/';
      i += 1;
      continue;
    }
    if (ch === COMPONENT_CATEGORY_PATH_SEPARATOR) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts;
}

export interface ComponentCategoryPathFields {
  hlavniKategorie: string | null;
  subkategorie: string | null;
  rozvetveni1: string | null;
  rozvetveni2: string | null;
  rozvetveni3: string | null;
  rozvetveni4: string | null;
}

const EMPTY_COMPONENT_CATEGORY_PATH_FIELDS: ComponentCategoryPathFields = {
  hlavniKategorie: null,
  subkategorie: null,
  rozvetveni1: null,
  rozvetveni2: null,
  rozvetveni3: null,
  rozvetveni4: null,
};

/** Cesta zařazení z polí `components` / listu Komponenty (max 6 úrovní). */
export function resolveComponentCategoryPathSegments(fields: {
  hlavniKategorie?: string | null;
  subkategorie?: string | null;
  rozvetveni1?: string | null;
  rozvetveni2?: string | null;
  rozvetveni3?: string | null;
  rozvetveni4?: string | null;
}): string[] {
  return [
    fields.hlavniKategorie,
    fields.subkategorie,
    fields.rozvetveni1,
    fields.rozvetveni2,
    fields.rozvetveni3,
    fields.rozvetveni4,
  ]
    .map(normalizeProductCategorySegment)
    .filter((p): p is string => p != null);
}

/** Sloupec Zařazení pro import/export (např. `Trubky/HDPE trubka/40/33`). */
export function formatComponentCategoryPath(fields: {
  hlavniKategorie?: string | null;
  subkategorie?: string | null;
  rozvetveni1?: string | null;
  rozvetveni2?: string | null;
  rozvetveni3?: string | null;
  rozvetveni4?: string | null;
}): string {
  return resolveComponentCategoryPathSegments(fields)
    .map(escapeComponentCategoryPathSegment)
    .join(COMPONENT_CATEGORY_PATH_SEPARATOR);
}

/** Parsuje sloupec Zařazení → legacy pole `components` (max 6 segmentů). */
export function parseComponentCategoryPathToFields(
  path: string | null | undefined,
): ComponentCategoryPathFields {
  const raw = String(path ?? '').trim();
  if (!raw) return { ...EMPTY_COMPONENT_CATEGORY_PATH_FIELDS };
  const segments = normalizeProductCategoryPathSegments(
    splitComponentCategoryPath(raw).map(unescapeComponentCategoryPathSegment),
  );
  return {
    hlavniKategorie: segments[0] ?? null,
    subkategorie: segments[1] ?? null,
    rozvetveni1: segments[2] ?? null,
    rozvetveni2: segments[3] ?? null,
    rozvetveni3: segments[4] ?? null,
    rozvetveni4: segments[5] ?? null,
  };
}

/** Legacy sloupce Hlavní Kategorie … Rozvětvení 4 → pole `components`. */
export function resolveComponentCategoryPathFieldsFromLegacyColumns(fields: {
  hlavniKategorie?: string | null;
  subkategorie?: string | null;
  rozvetveni1?: string | null;
  rozvetveni2?: string | null;
  rozvetveni3?: string | null;
  rozvetveni4?: string | null;
}): ComponentCategoryPathFields {
  const segments = resolveComponentCategoryPathSegments(fields);
  return {
    hlavniKategorie: segments[0] ?? null,
    subkategorie: segments[1] ?? null,
    rozvetveni1: segments[2] ?? null,
    rozvetveni2: segments[3] ?? null,
    rozvetveni3: segments[4] ?? null,
    rozvetveni4: segments[5] ?? null,
  };
}


/**
 * Výchozí subkategorie 1. úrovně pod kořeny podle typu objektu.
 * Obecné se plní z listu Komponenty (`ensureObecneCatalogCategories`).
 */
export const DEFAULT_PRODUCT_CATEGORY_SUBS: Record<
  ProductCategoryRootKey,
  readonly string[]
> = {
  OBECNE: [],
  SPOJKOVISTE: [
    'Trubky a chráničky',
    'Mikrotrubičky',
    'Těsnění a ucpávky',
    'Koncovky',
    'Spojky a redukce',
    'Markery',
  ],
  BUDOVA: [
    'Mikrotrubičky',
    'Koncovky a ucpávky',
    'Spojky a redukce',
    'Trubky a chráničky',
    'Kabely',
    'Boxy',
    'Optické pasivní komponenty',
    'Stavební, montážní a ochraný materiál',
    'Matrix a Markery',
    'Kabelové komory',
    'Záfuk',
  ],
  VYKOP: [
    'Trubky',
    'Mikrotrubičky',
    'Svazky mikrotrubiček',
    'Kabelové komory',
    'Skříně, rozvaděče a boxy',
  ],
};

export function isProductCategoryObjectTypeKey(
  raw: string | null | undefined,
): raw is ProductCategoryObjectTypeKey {
  return parseProductCategoryObjectTypeKey(raw) != null;
}

export function isProductCategoryRootKey(
  raw: string | null | undefined,
): raw is ProductCategoryRootKey {
  const key = String(raw ?? '')
    .trim()
    .toUpperCase();
  return key === 'OBECNE' || isProductCategoryObjectTypeKey(key);
}

export interface MaterialProductCategoryDto {
  id: string;
  name: string;
  parentId: string | null;
  /** Jen u kořenů: OBECNE | SPOJKOVISTE | BUDOVA | VYKOP. */
  objectTypeKey: ProductCategoryRootKey | null;
  sortOrder: number;
  /** Systémové kořeny nelze smazat. */
  isSystem: boolean;
}

export interface MaterialProductCategoryCreateInput {
  name: string;
  parentId?: string | null;
  sortOrder?: number;
}

export interface MaterialProductCategoryUpdateInput {
  name?: string;
  parentId?: string | null;
  sortOrder?: number;
}
